import {ChevronRightIcon, GearIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, SegmentedControl} from '@primer/react'
import type React from 'react'
import {type ReactNode, useId} from 'react'

import {findField, type FieldOption} from './field-option'
import styles from './ViewOptionsMenu.module.css'

export interface ViewOptionsMenuProps {
  /** Accessible name for the trigger button. */
  triggerLabel: string
  /**
   * The consumer-composed overlay content. Compose `<ViewOptionsMenu.Layout>` first, followed
   * by a `<ViewOptionsMenu.List>` containing menu rows, groups, and dividers.
   */
  children: ReactNode
}

/**
 * Agnostic shell for a view-options menu: a gear-icon trigger and an anchored overlay. The
 * shell deliberately does not wrap or inspect its children: a surface makes the structure
 * and menu semantics explicit by composing `<ViewOptionsMenu.Layout>`,
 * `<ViewOptionsMenu.List>`, groups, and dividers.
 */
export function ViewOptionsMenu({triggerLabel, children}: ViewOptionsMenuProps) {
  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton icon={GearIcon} aria-label={triggerLabel} />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay
        align="end"
        width="medium"
        variant={{regular: 'anchored', narrow: 'fullscreen'}}
        className={styles.overlay}
      >
        {children}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

export interface LayoutOption<TValue extends string = string> {
  value: TValue
  label: string
  icon?: React.ComponentType<{size?: number}>
}

export interface ViewOptionsLayoutProps<TValue extends string = string> {
  /** Section heading. */
  label: string
  /** Optional trailing content in the header, e.g. a status badge. */
  trailing?: ReactNode
  /** The selectable layouts, in display order. */
  options: ReadonlyArray<LayoutOption<TValue>>
  /** The currently selected layout. */
  value: TValue
  onSelect: (value: TValue) => void
}

/**
 * A labelled segmented control for switching layouts. Render it before
 * `<ViewOptionsMenu.List>` so layout retains its consistent position and visual treatment.
 * The component is agnostic of what the layouts are — a surface supplies its own set
 * (list/board, table/board/roadmap, …) and maps the reported value onto its own state.
 */
function Layout<TValue extends string = string>({
  label,
  trailing,
  options,
  value,
  onSelect,
}: ViewOptionsLayoutProps<TValue>) {
  const labelId = useId()

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <span id={labelId} className={styles.sectionLabel}>
          {label}
        </span>
        {trailing}
      </div>
      <div className={styles.layout} role="group" aria-labelledby={labelId}>
        <SegmentedControl
          aria-label={label}
          size="small"
          fullWidth
          onChange={index => {
            const option = options[index]
            if (option) onSelect(option.value)
          }}
        >
          {options.map(option => {
            const Icon = option.icon
            return (
              <SegmentedControl.Button
                key={option.value}
                selected={option.value === value}
                leadingVisual={Icon ? () => <Icon /> : undefined}
              >
                {option.label}
              </SegmentedControl.Button>
            )
          })}
        </SegmentedControl>
      </div>
    </div>
  )
}

export interface ViewOptionsListProps {
  children: ReactNode
}

/**
 * The menu-list region below the fixed layout slot. Consumers place groups, rows, and
 * dividers inside it explicitly so the rendered `ActionList` structure is visible at the
 * composition site.
 */
function List({children}: ViewOptionsListProps) {
  return <ActionList className={styles.list}>{children}</ActionList>
}

export interface ViewOptionsGroupProps {
  /** Visible group heading, also the accessible name of the group. */
  label: string
  /** One or more `<ViewOptionsMenu.SingleSelect>` rows. */
  children: ReactNode
}

/**
 * A labelled section for row primitives. Must be rendered inside a
 * `<ViewOptionsMenu.List>`.
 */
function Group({label, children}: ViewOptionsGroupProps) {
  return (
    <ActionList.Group aria-label={label}>
      <ActionList.GroupHeading>{label}</ActionList.GroupHeading>
      {children}
    </ActionList.Group>
  )
}

export interface ViewOptionsSingleSelectProps {
  /** The row's leading icon. */
  icon: React.ComponentType<{size?: number}>
  /** The row's visible label, e.g. "Group by". Also names the submenu. */
  label: string
  /** Shown as the current value, and as the submenu prompt, when nothing is selected. */
  placeholder: string
  /** The selectable fields, in display order. */
  fields: readonly FieldOption[]
  /** Identifier of the selected field, or `null` when nothing is chosen. */
  value: string | null
  onSelect: (identifier: string) => void
}

/**
 * A single-select row that opens a submenu of fields — the shape "Group by", "Swimlane by",
 * and "Sort by" all share. The row shows the current selection beside a chevron, and the
 * submenu marks the selected field. When `value` is `null` the row shows `placeholder` and
 * the submenu marks nothing, prompting a choice rather than inventing a default. Draws
 * nothing when handed no fields. Must be rendered inside a `<ViewOptionsMenu.Group>`.
 */
function SingleSelect({icon: Icon, label, placeholder, fields, value, onSelect}: ViewOptionsSingleSelectProps) {
  if (fields.length === 0) return null

  const selected = findField(fields, value)
  const currentLabel = selected?.label ?? placeholder

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <ActionList.Item>
          <ActionList.LeadingVisual>
            <Icon />
          </ActionList.LeadingVisual>
          {label}
          <ActionList.TrailingVisual>
            <span className={styles.trailingValue}>
              {currentLabel}
              <ChevronRightIcon />
            </span>
          </ActionList.TrailingVisual>
        </ActionList.Item>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium">
        <ActionList selectionVariant="single" aria-label={label}>
          {fields.map(field => {
            const FieldIcon = field.icon
            return (
              <ActionList.Item
                key={field.identifier}
                selected={field.identifier === value}
                onSelect={() => onSelect(field.identifier)}
              >
                <ActionList.LeadingVisual>
                  <FieldIcon />
                </ActionList.LeadingVisual>
                {field.label}
              </ActionList.Item>
            )
          })}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

export interface ViewOptionsToggleGroupProps {
  /** Visible group heading, also the accessible name of the group. */
  label: string
  /** The toggleable fields, in display order. */
  fields: readonly FieldOption[]
  /** Identifiers of the fields currently on. */
  selectedIds: ReadonlySet<string>
  onToggle: (identifier: string, next: boolean) => void
}

/**
 * A self-contained labelled group of binary-state rows, one per field, reporting whether each
 * is on. The rows are `menuitemcheckbox`es under a `selectionVariant="multiple"` group so
 * Space and Enter toggle them and the change announces through `aria-checked`; toggling a row
 * keeps the parent menu open so the user can flip several fields without reopening it. Must
 * be rendered inside a `<ViewOptionsMenu.List>`.
 */
function ToggleGroup({label, fields, selectedIds, onToggle}: ViewOptionsToggleGroupProps) {
  return (
    <ActionList.Group selectionVariant="multiple" aria-label={label}>
      <ActionList.GroupHeading>{label}</ActionList.GroupHeading>
      {fields.map(field => {
        const Icon = field.icon
        const selected = selectedIds.has(field.identifier)
        return (
          <ActionList.Item
            key={field.identifier}
            selected={selected}
            onSelect={event => {
              // Prevents menu from closing.
              event.preventDefault()
              onToggle(field.identifier, !selected)
            }}
          >
            <ActionList.LeadingVisual>
              <Icon />
            </ActionList.LeadingVisual>
            {field.label}
          </ActionList.Item>
        )
      })}
    </ActionList.Group>
  )
}

/** A separator between groups or rows inside a `<ViewOptionsMenu.List>`. */
function Divider() {
  return <ActionList.Divider />
}

ViewOptionsMenu.Layout = Layout
ViewOptionsMenu.List = List
ViewOptionsMenu.Group = Group
ViewOptionsMenu.SingleSelect = SingleSelect
ViewOptionsMenu.ToggleGroup = ToggleGroup
ViewOptionsMenu.Divider = Divider
